import isEqual from 'fast-deep-equal';
import { Mode, MORE_OPTION_REVAMP_TAB, FLAG } from 'enums';
import {
  ACTIVITY_TYPES,
  MAXIMUM_ALLOWED_MINUTES,
  MORE_OPTION_TAB,
  PLATFORMS
} from 'constants/general';
import { ActivityType } from 'interfaces/common.interface';
import { SettingsType, Video_Metadata } from 'interfaces/setting.interface';
import { isActivityChoicesDataValid } from 'utils/activity-choices.util';
import { areActivityImagesValid } from 'utils/activity-images.util';
import {
  areDaysOfWeekSetsEqual,
  moreOptionsDaysOfWeekIsInvalid
} from 'utils/daysOfWeek.util';
import {
  convertToSeconds,
  getRoutineActivitiesOfCurrentSettings,
  getYoutubeURLId
} from 'utils/helpers';
import {
  activityHasPerChoiceLogging,
  areLogQuantityQuestionsValid
} from 'utils/log-quantity-questions.util';
import {
  hasMeaningfulCutoff,
  isLateCutoffRequired,
  isActivityCutoffTimeValid
} from 'utils/validators';
import moment from 'moment';
import { ROUTES } from 'constants/routes';
import { isCurrentPath, isRouteEmbedded } from 'utils/routes.util';
import { isMobilePlatform } from 'utils/settings.util';

export type MoreOptionsModalContext = {
  activity?: ActivityType;
  type: string;
  position: number;
  currentSettings: SettingsType;
  mode: Mode;
  activityLibrary: ActivityType[];
  customRoutinePosition?: number;
};

export type MoreOptionsModalActionState = {
  isDirty: boolean;
  isValid: boolean;
  canSave: boolean;
  isDaysOfWeekValid: boolean;
};

export type PersistedMoreOptionActivityContext = {
  activity?: ActivityType;
  type: string;
  previousSettings: SettingsType;
  previousActivityLibrary: ActivityType[];
};

export const getOriginalMoreOptionActivity = ({
  type,
  position,
  currentSettings,
  mode,
  activityLibrary,
  customRoutinePosition
}: Omit<MoreOptionsModalContext, 'activity'>): ActivityType | undefined => {
  const routineActivities =
    mode === Mode.LIBRARY
      ? activityLibrary
      : getRoutineActivitiesOfCurrentSettings(
          type,
          currentSettings,
          customRoutinePosition
        );
  return routineActivities?.[position];
};

export const isMoreOptionActivityPersisted = ({
  activity,
  type,
  previousSettings,
  previousActivityLibrary
}: PersistedMoreOptionActivityContext): boolean => {
  const activityId = activity?.id;

  if (!activityId) {
    return false;
  }

  if (type === ACTIVITY_TYPES.LIBRARY) {
    return previousActivityLibrary.some((item) => item.id === activityId);
  }

  if (type === ACTIVITY_TYPES.CUSTOM_ROUTINES) {
    return (
      previousSettings.custom_routines?.some((routine) =>
        routine.standalone_activities?.some((item) => item.id === activityId)
      ) ?? false
    );
  }

  return getRoutineActivitiesOfCurrentSettings(type, previousSettings).some(
    (item) => item.id === activityId
  );
};

export const isMoreOptionsDirty = (
  activity: ActivityType | undefined,
  original: ActivityType | undefined
): boolean => {
  if (!activity || !original) {
    return false;
  }

  if (!areDaysOfWeekSetsEqual(activity.days_of_week, original.days_of_week)) {
    return true;
  }

  const { days_of_week: _activityDays, ...activityRest } = activity;
  const { days_of_week: _originalDays, ...originalRest } = original;

  return !isEqual(activityRest, originalRest);
};

export const isMoreOptionsFormValid = ({
  activity
}: {
  activity?: ActivityType;
}): boolean => {
  if (!activity) {
    return false;
  }

  const isDurationValid =
    Boolean(activity.duration_seconds) &&
    activity.duration_seconds > 0 &&
    activity.duration_seconds <= convertToSeconds(MAXIMUM_ALLOWED_MINUTES);

  const isActivityNameValid = Boolean(activity.name?.trim());

  // Toggle on requires a time; the value itself is not bounded by the global cutoff.
  const isCutoffMissing =
    isLateCutoffRequired(activity) &&
    !hasMeaningfulCutoff(activity.cutoff_time_for_doing_activity);

  const isCutoffTimeValid =
    !isCutoffMissing &&
    isActivityCutoffTimeValid(activity.cutoff_time_for_doing_activity);

  const isDaysOfWeekValid = !moreOptionsDaysOfWeekIsInvalid(
    activity.days_of_week
  );

  const isParentLogValid = activityHasPerChoiceLogging(activity)
    ? true
    : areLogQuantityQuestionsValid(
        activity.log_quantity_questions,
        activity.log_quantity
      );
  const areImagesValid = areActivityImagesValid(activity.image_urls);

  return (
    areImagesValid &&
    isParentLogValid &&
    isActivityChoicesDataValid(activity) &&
    isDurationValid &&
    isActivityNameValid &&
    isCutoffTimeValid &&
    isDaysOfWeekValid
  );
};

export const getMoreOptionsModalActionState = (
  ctx: MoreOptionsModalContext
): MoreOptionsModalActionState => {
  const original = getOriginalMoreOptionActivity(ctx);
  const isDirty = isMoreOptionsDirty(ctx.activity, original);
  const isDaysOfWeekValid = !moreOptionsDaysOfWeekIsInvalid(
    ctx.activity?.days_of_week
  );
  const isValid = isMoreOptionsFormValid({ activity: ctx.activity });

  return {
    isDirty,
    isValid,
    canSave: isDirty && isValid,
    isDaysOfWeekValid
  };
};

const parseVideoDurationSeconds = (raw?: string): number => {
  if (!raw) {
    return 0;
  }

  if (/^PT/i.test(raw)) {
    return moment.duration(raw).asSeconds();
  }

  const parts = raw.split(':').map((part) => parseInt(part, 10));

  if (parts.length === 2) {
    return moment
      .duration({ minutes: parts[0], seconds: parts[1] })
      .asSeconds();
  }

  if (parts.length === 3) {
    return moment
      .duration({ hours: parts[0], minutes: parts[1], seconds: parts[2] })
      .asSeconds();
  }

  return moment.duration(raw).asSeconds();
};

const findVideoMetadataForUrl = (
  url: string,
  videos_metadata: Video_Metadata[]
): Video_Metadata | undefined => {
  const videoId = getYoutubeURLId(url);

  if (videoId) {
    const byId = videos_metadata.find(
      (meta) =>
        meta.id === videoId || getYoutubeURLId(meta.video_url) === videoId
    );
    if (byId) {
      return byId;
    }
  }

  return videos_metadata.find((meta) => meta.video_url === url);
};

export const getActivityVideoDurationWarning = (
  activity: ActivityType | undefined,
  videos_metadata: Video_Metadata[]
): { showWarning: boolean; videoDurationSeconds?: number } => {
  if (!activity?.duration_seconds || !activity?.video_urls?.length) {
    return { showWarning: false };
  }

  const activityDurationSeconds = activity.duration_seconds;
  let videoDurationSeconds: number | undefined;

  const showWarning = activity.video_urls.some((url) => {
    const metadata = findVideoMetadataForUrl(url, videos_metadata);
    if (!metadata?.duration) {
      return false;
    }

    const seconds = parseVideoDurationSeconds(metadata.duration);

    if (seconds < activityDurationSeconds) {
      videoDurationSeconds = seconds;
      return true;
    }

    return false;
  });

  return { showWarning, videoDurationSeconds };
};

export type MoreOptionTabId = MORE_OPTION_REVAMP_TAB;

export const MORE_OPTION_REVAMP_TAB_IDS: MORE_OPTION_REVAMP_TAB[] = [
  MORE_OPTION_REVAMP_TAB.TIMING,
  MORE_OPTION_REVAMP_TAB.VIDEOS,
  MORE_OPTION_REVAMP_TAB.ALLOWED_APPS,
  MORE_OPTION_REVAMP_TAB.LOG_DATA,
  MORE_OPTION_REVAMP_TAB.ACTIVITY_CHOICES,
  MORE_OPTION_REVAMP_TAB.INSTRUCTIONS,
  MORE_OPTION_REVAMP_TAB.ADVANCED
];

export const REVAMP_TAB_TO_LEGACY_TITLE: Record<MORE_OPTION_REVAMP_TAB, string> =
  {
    [MORE_OPTION_REVAMP_TAB.TIMING]: MORE_OPTION_TAB.TIMING,
    [MORE_OPTION_REVAMP_TAB.VIDEOS]: MORE_OPTION_TAB.VIDEOS,
    [MORE_OPTION_REVAMP_TAB.ALLOWED_APPS]: MORE_OPTION_TAB.ALLOWED_APPS,
    [MORE_OPTION_REVAMP_TAB.LOG_DATA]: MORE_OPTION_TAB.LOG_DATA,
    [MORE_OPTION_REVAMP_TAB.ACTIVITY_CHOICES]: MORE_OPTION_TAB.SETTINGS,
    [MORE_OPTION_REVAMP_TAB.INSTRUCTIONS]: MORE_OPTION_TAB.INSTRUCTIONS,
    [MORE_OPTION_REVAMP_TAB.ADVANCED]: MORE_OPTION_TAB.ADVANCED
  };

export type MoreOptionsTabVisibilityContext = {
  activityType: string;
  platform: string;
  flags: string[];
};

export const isLegacyMoreOptionTabVisible = (
  legacyTabTitle: string,
  context: MoreOptionsTabVisibilityContext
): boolean => {
  const { activityType, platform, flags } = context;

  if (flags.includes(FLAG.GEEK_MODE)) {
    return activityType !== ACTIVITY_TYPES.BREAKING &&
      !isMobilePlatform(platform)
      ? true
      : legacyTabTitle !== MORE_OPTION_TAB.ALLOWED_APPS;
  }

  if (
    isRouteEmbedded ||
    isMobilePlatform(platform) ||
    isCurrentPath(ROUTES.ONBOARD_FLOW)
  ) {
    return [MORE_OPTION_TAB.TIMING, MORE_OPTION_TAB.VIDEOS].includes(
      legacyTabTitle
    );
  }

  return platform === PLATFORMS.WEB;
};

export const getVisibleMoreOptionTabIds = (
  tabIds: MoreOptionTabId[],
  context: MoreOptionsTabVisibilityContext
): MoreOptionTabId[] =>
  tabIds.filter((tabId) =>
    isLegacyMoreOptionTabVisible(REVAMP_TAB_TO_LEGACY_TITLE[tabId], context)
  );

/** Legacy Settings tab visibility matches other standard tabs (no breaking-only gate). */
export const getMoreOptionsVisibleTabIds = ({
  context,
  allTabIds = MORE_OPTION_REVAMP_TAB_IDS
}: {
  context: MoreOptionsTabVisibilityContext;
  allTabIds?: MoreOptionTabId[];
}): MoreOptionTabId[] => getVisibleMoreOptionTabIds(allTabIds, context);
